import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarBlank, Users, Phone, ChatCircle, Armchair } from "@phosphor-icons/react";
import { formatApiError } from "@/utils/errorFormat";

/**
 * ReservationsPanel — dialog opened from the POS top bar so servers can
 * see today's reservations and assign / change / seat a table for each
 * one without leaving the order screen.
 *
 * We deliberately do NOT let servers create/edit guest names, phones,
 * party sizes, etc. from here — that's the Reservations page's job.
 * This panel only cares about the day-of workflow: "party X is here,
 * put them on table Y and mark them seated".
 */
const STATUS_STYLES = {
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  seated:    "bg-green-100 text-green-800 border-green-200",
  completed: "bg-gray-100 text-gray-700 border-gray-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  no_show:   "bg-orange-100 text-orange-800 border-orange-200",
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtTime = (t) => {
  // Accept "HH:MM" (24h) and format to 12h w/ AM/PM
  if (!t || !/^\d{2}:\d{2}/.test(t)) return t || "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
};

const ReservationsPanel = ({ open, onOpenChange, tables = [], onTablesChanged }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [date, setDate] = useState(todayStr());

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reservations`, { params: { date } });
      // Show confirmed + seated first, others below; within a group sort by time asc.
      const rows = res.data || [];
      const rank = (s) => (s === "confirmed" ? 0 : s === "seated" ? 1 : 2);
      rows.sort((a, b) => rank(a.status) - rank(b.status) || (a.reservation_time || "").localeCompare(b.reservation_time || ""));
      setReservations(rows);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load reservations"));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    if (open) fetchReservations();
  }, [open, fetchReservations]);

  const patchReservation = async (resId, payload, successMsg) => {
    setSavingId(resId);
    try {
      const res = await axios.put(`${API}/reservations/${resId}`, payload);
      setReservations((prev) => prev.map((r) => (r.id === resId ? { ...r, ...res.data } : r)));
      if (successMsg) toast.success(successMsg);
      // Table status changed on the backend — refresh the floor plan.
      if (onTablesChanged) onTablesChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setSavingId(null);
    }
  };

  const handleAssignTable = (resId, tableId) => {
    const table = tables.find((t) => t.id === tableId);
    patchReservation(resId, { table_id: tableId }, table ? `Assigned to ${table.name}` : "Table assigned");
  };

  const handleSeat = (r) => {
    if (!r.table_id) {
      toast.warning("Pick a table first — can't seat a party without one");
      return;
    }
    patchReservation(r.id, { status: "seated" }, `${r.guest_name} seated at ${r.table_name || "table"}`);
  };

  const handleNoShow = (r) => {
    if (!window.confirm(`Mark ${r.guest_name}'s reservation as no-show?`)) return;
    patchReservation(r.id, { status: "no_show" }, "Marked as no-show");
  };

  const handleComplete = (r) => {
    patchReservation(r.id, { status: "completed" }, "Reservation completed");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="reservations-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarBlank size={20} weight="fill" className="text-primary" />
            Reservations
          </DialogTitle>
        </DialogHeader>

        {/* Date shifter */}
        <div className="flex items-center gap-2 mb-3">
          <Button
            variant="outline" size="sm"
            onClick={() => {
              const d = new Date(date);
              d.setDate(d.getDate() - 1);
              setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
            }}
            data-testid="res-date-prev"
          >←</Button>
          <div className="font-mono text-sm px-3 py-1.5 bg-secondary/50 rounded-md" data-testid="res-date-label">
            {date === todayStr() ? "Today · " : ""}{date}
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => {
              const d = new Date(date);
              d.setDate(d.getDate() + 1);
              setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
            }}
            data-testid="res-date-next"
          >→</Button>
          {date !== todayStr() && (
            <Button variant="ghost" size="sm" onClick={() => setDate(todayStr())} data-testid="res-date-today">Today</Button>
          )}
        </div>

        <div className="overflow-y-auto flex-1 space-y-2 pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <CalendarBlank size={40} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No reservations for {date === todayStr() ? "today" : date}</p>
            </div>
          ) : (
            reservations.map((r) => {
              const isTerminal = ["completed", "cancelled", "no_show"].includes(r.status);
              const isSaving = savingId === r.id;
              return (
                <div
                  key={r.id}
                  className={`bg-white border border-border rounded-xl p-3 ${isTerminal ? "opacity-60" : ""}`}
                  data-testid={`reservation-row-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-medium">{r.guest_name}</span>
                        <Badge variant="outline" className={STATUS_STYLES[r.status] || ""}>
                          {r.status.replace("_", " ")}
                        </Badge>
                        <span className="font-mono text-sm text-muted-foreground">{fmtTime(r.reservation_time)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span className="inline-flex items-center gap-1"><Users size={12} />{r.party_size}</span>
                        {r.guest_phone && <span className="inline-flex items-center gap-1"><Phone size={12} />{r.guest_phone}</span>}
                        {r.notes && <span className="inline-flex items-center gap-1"><ChatCircle size={12} />{r.notes}</span>}
                      </div>
                    </div>

                    {/* Table picker + actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={r.table_id || "none"}
                        onValueChange={(v) => handleAssignTable(r.id, v === "none" ? null : v)}
                        disabled={isSaving || isTerminal}
                      >
                        <SelectTrigger className="w-44 h-9" data-testid={`res-table-select-${r.id}`}>
                          <SelectValue placeholder="Pick a table" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— No table —</SelectItem>
                          {tables.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} · {t.seats} seats {t.seats < r.party_size ? "⚠︎" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {r.status === "confirmed" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleSeat(r)}
                            disabled={isSaving || !r.table_id}
                            className="gap-1"
                            data-testid={`res-seat-${r.id}`}
                            title={!r.table_id ? "Assign a table first" : "Seat this party"}
                          >
                            <Armchair size={14} weight="fill" /> Seat
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleNoShow(r)}
                            disabled={isSaving}
                            className="text-orange-700 border-orange-200"
                            data-testid={`res-noshow-${r.id}`}
                          >No-show</Button>
                        </>
                      )}
                      {r.status === "seated" && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleComplete(r)}
                          disabled={isSaving}
                          data-testid={`res-complete-${r.id}`}
                        >Complete</Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationsPanel;
